import React, { ChangeEvent, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import useNswagClient from './../../hooks/api/useNswagClient';
import { UserContext } from './../../components/shared/useUser';
import useLogError from './../../hooks/useLogError';
import { useTranslation } from 'react-i18next';
import { Franchise, Store } from './../../app/services/api/generated';
import { compare } from './../../utils';
import { PodConfigurationModel, podClient } from './../../app/services/api/podClient';
import PageArea from './../../components/shared/PageArea';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Grid, InputAdornment, MenuItem, Select, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useTheme, Tooltip } from '@mui/material';
import PageTitle from './../../components/shared/PageTitle';
import LoadingWheel from './../../components/ui/LoadingWheel';
import { ConfigurationLevel } from './enums/ConfigurationLevel';
import { ErrorOutline, ExpandMore, HelpOutline } from '@mui/icons-material';
import CustomPagination from './../../components/forms/Pagination';
import { mergeArrays } from './helpers/ArrayHelper';
import StoreConfigurationsList from './components/StoreConfigurationsList';
import MessagesSnackbar from './../../components/shared/MessagesSnackbar';
import { SearchSm } from './../../assets';
import FranchiseConfigurationsList from './components/FranchiseConfigurationsList';
import { Item, ItemFilterParams, client } from './../../app/services/api/orderManagementClient';
import PodConfigurationInfo from './components/modals/PodConfigurationInfo';
import CustomSwitch from './../../components/shared/CustomSwitch';

type ItemToggleState = 'none' | 'some' | 'all';

const ManagePodConfigurations = () => {

  const { t } = useTranslation('managePodConfigurations');
  const { user, isAdmin, hasPermissionTo, selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [allFranchises, setAllFranchises] = useState<Franchise[]>([]);
  const [selectedFranchise, setSelectedFranchise] = useState<Franchise | null>(null);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [allItems, setAllItems] = useState<Item[]>([]);
  const [allConfigurations, setAllConfigurations] = useState<PodConfigurationModel[]>([]);
  const [existingConfigurations, setExistingConfigurations] = useState<PodConfigurationModel[]>([]);
  const [existingStoreConfigurations, setExistingStoreConfigurations] = useState<PodConfigurationModel[]>([]);
  const [availableConfigs, setAvailableConfigs] = useState<PodConfigurationModel[]>([]);
  const [pickedStore, setPickedStore] = useState<Store | null>(null);
  const [configurationLevel, setConfigurationLevel] = useState<ConfigurationLevel | null>(null);
  const [initialConfigurations, setInitialConfigurations] = useState<PodConfigurationModel[]>([]);
  const [isPodInfoModalOpen, setIsPodInfoModalOpen] = useState<boolean>(false);
  const [, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [allStoreItemConfigs, setAllStoreItemConfigs] = useState<PodConfigurationModel[]>([]);


  const [franchiseStoreConfigurations, setFranchiseStoreConfigurations] = useState<PodConfigurationModel[]>([]);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
      open: boolean;
      message: string;
    }>({ open: false, message: '' });

  const { getFranchises, getFranchiseStores } = useNswagClient();

  /** Store manager: has AIOrderingStore but not AIOrderingAdmin. Used to keep store managers scoped to their own store. */
  const isStoreManager = hasPermissionTo(['AIOrderingStore']) && !hasPermissionTo(['AIOrderingAdmin']);

  const canEditConfigFields = isStoreManager;

  /** Enabling/disabling POD for a store, franchise, or item is an Ops decision — AI Admin only. */
  const canToggleEnabled = hasPermissionTo(['AIOrderingAdmin']);

  /** Builds a unique key for a POD configuration based on franchise, store, and item. */
  const getConfigKey = (config: PodConfigurationModel) => 
    `${config.franchiseName}-${config.storeNumber}-${config.itemNumber}`;

  /** Resolves the POD itemNumber for an item: gtin when present, else sku. TODO: OM119ish issue. Consider replacing with 'usesSkuMapping' logic when available. */
  const getItemNumber = (item: Item): string => (item.gtin && item.gtin.trim() !== '' ? item.gtin : item.sku ?? '');

  /** Returns stores that are enabled for predictive ordering. */
  const getEnabledStores = useCallback((): Store[] => {
    const enabledStoreNumbers = new Set(
      existingStoreConfigurations
        .filter(c => !c.itemNumber && c.enabled)
        .map(c => c.storeNumber),
    );
    return allStores.filter(store => enabledStoreNumbers.has(store.storeNumber ?? ''));
  }, [existingStoreConfigurations, allStores]);

  /** Finds stores that do not yet have a configuration for the given item. */
  const getStoresWithoutConfig = (itemNumber: string): Store[] => {
    if (configurationLevel !== ConfigurationLevel.Franchise) return [];
    const enabledStores = getEnabledStores();
    const storesWithConfig = new Set(
      allStoreItemConfigs
        .filter(c => c.itemNumber === itemNumber &&
                    c.franchiseName === selectedFranchise?.franchiseName)
        .map(c => c.storeNumber),
    );
    return enabledStores.filter(store => !storesWithConfig.has(store.storeNumber ?? ''));
  };

  /** Returns the toggle state of an item across stores: 'none', 'some', or 'all'. */
  const getItemStoreEnabledState = (itemNumber: string): ItemToggleState => {
    if (configurationLevel !== ConfigurationLevel.Franchise) return 'none';
    if (allStores.length === 0) return 'none';
    const enabledStores = getEnabledStores();
    if (enabledStores.length === 0) return 'none';
    const enabledStoreNumbers = new Set(enabledStores.map(s => s.storeNumber ?? ''));
    const storeConfigsForItem = allStoreItemConfigs.filter(
      c => c.itemNumber === itemNumber &&
                c.enabled &&
                c.franchiseName === selectedFranchise?.franchiseName &&
                enabledStoreNumbers.has(c.storeNumber),
    );
    const enabledCount = storeConfigsForItem.length;
    if (enabledCount === 0) return 'none';
    if (enabledCount >= enabledStores.length) return 'all';
    return 'some';
  };

  // --- Shared cascade helpers ---

  /** Builds cascade create/update arrays for enabling or disabling a franchise-level ITEM config across stores. */
  const buildItemCascadeOps = (item: PodConfigurationModel) => {
    const toCreate: PodConfigurationModel[] = [];
    const toUpdate: PodConfigurationModel[] = [];

    if (!item.enabled) {
      // Disabling → disable all enabled store configs for this item
      allStoreItemConfigs
        .filter(c => c.itemNumber === item.itemNumber && c.enabled && c.franchiseName === selectedFranchise?.franchiseName)
        .forEach(config => toUpdate.push({ ...config, enabled: false }));
    } else {
      // Enabling → create for stores without config, re-enable existing disabled configs
      const enabledStores = getEnabledStores();
      getStoresWithoutConfig(item.itemNumber).forEach(store =>
        toCreate.push({
          ...item, id: 0, configurationLevel: ConfigurationLevel.Store,
          storeNumber: store.storeNumber ?? '', storeName: store.storeName ?? '',
          starbucksStoreNumber: store.starbucksStoreNumber ?? '',
        }),
      );
      allStoreItemConfigs
        .filter(c => c.itemNumber === item.itemNumber && !c.enabled &&
          c.franchiseName === selectedFranchise?.franchiseName &&
          enabledStores.some(store => store.storeNumber === c.storeNumber))
        .forEach(config => toUpdate.push({ ...config, enabled: true }));
    }

    return { toCreate, toUpdate };
  };

  /** Builds cascade create/update arrays for enabling or disabling a STORE config at franchise level. */
  const buildStoreCascadeOps = (storeConfig: PodConfigurationModel) => {
    const toCreate: PodConfigurationModel[] = [];
    const toUpdate: PodConfigurationModel[] = [];

    if (storeConfig.enabled) {
      const existingItemNumbers = new Set(
        allStoreItemConfigs
          .filter(c => c.storeNumber === storeConfig.storeNumber && c.itemNumber)
          .map(c => c.itemNumber),
      );
      existingConfigurations
        .filter(c => c.itemNumber && c.enabled && !existingItemNumbers.has(c.itemNumber))
        .forEach(config => toCreate.push({
          ...config, id: 0, configurationLevel: ConfigurationLevel.Store,
          storeNumber: storeConfig.storeNumber, storeName: storeConfig.storeName,
          starbucksStoreNumber: storeConfig.starbucksStoreNumber,
        }));
    } else {
      allStoreItemConfigs
        .filter(c => c.storeNumber === storeConfig.storeNumber && c.enabled && c.franchiseName === selectedFranchise?.franchiseName)
        .forEach(config => toUpdate.push({ ...config, enabled: false }));
    }

    return { toCreate, toUpdate };
  };

  /** Normalizes empty-string quantity fields to null. Returns a new object (does not mutate the input). */
  const normalizeQuantityFields = (item: PodConfigurationModel): PodConfigurationModel => ({
    ...item,
    minStockOnHandQuantity: item.minStockOnHandQuantity === '' ? null : item.minStockOnHandQuantity,
    maxStockOnHandQuantity: item.maxStockOnHandQuantity === '' ? null : item.maxStockOnHandQuantity,
  });

  /** Normalizes quantity fields for comparison, coercing numeric strings to numbers. */
  const normalizeForCompare = (config: PodConfigurationModel) => ({
    ...config,
    minStockOnHandQuantity:
      config.minStockOnHandQuantity === '' || config.minStockOnHandQuantity == null
        ? null
        : Number(config.minStockOnHandQuantity),
    maxStockOnHandQuantity:
      config.maxStockOnHandQuantity === '' || config.maxStockOnHandQuantity == null
        ? null
        : Number(config.maxStockOnHandQuantity),
  });

  const changedConfigurations = useMemo((): PodConfigurationModel[] => {
    const initialMap = new Map(initialConfigurations.map(c => [getConfigKey(c), c]));
    return allConfigurations.filter(current => {
      const initial = initialMap.get(getConfigKey(current));
      if (!initial) return true; // New config
      return JSON.stringify(normalizeForCompare(current)) !== JSON.stringify(normalizeForCompare(initial));
    });
  }, [allConfigurations, initialConfigurations]);

  const hasAnyPendingChanges = changedConfigurations.length > 0;

  /** Reverts all configurations to their initial state. */
  const handleCancelAll = () => {
    setAllConfigurations([...initialConfigurations]);
    setValidationErrors({});
  };

  /** Computes aggregated cascade information for batch save confirmation dialogs. */
  const getAggregatedCascadeInfo = (): { needsConfirmation: boolean; message: string } => {
    if (configurationLevel !== ConfigurationLevel.Franchise) {
      return { needsConfirmation: false, message: '' };
    }
    const changedConfigs = changedConfigurations.filter(c => c.itemNumber); // Only item configs
    const enabledStores = getEnabledStores();
    if (enabledStores.length === 0) {
      return { needsConfirmation: false, message: '' };
    }
    const storesToEnable = new Set<string>();
    const storesToDisable = new Set<string>();
    const itemsToEnable: string[] = [];
    const itemsToDisable: string[] = [];
    const initialMap = new Map(initialConfigurations.map(c => [getConfigKey(c), c]));
    changedConfigs.forEach(current => {
      const initial = initialMap.get(getConfigKey(current));
      const wasEnabled = initial?.enabled ?? false;
      const isNowEnabled = current.enabled;
      if (!wasEnabled && isNowEnabled) {
        // Enabling
        const storesWithoutConfig = getStoresWithoutConfig(current.itemNumber);
        const disabledStoreConfigs = allStoreItemConfigs.filter(
          c => c.itemNumber === current.itemNumber && !c.enabled &&
              c.franchiseName === selectedFranchise?.franchiseName &&
              enabledStores.some(store => store.storeNumber === c.storeNumber),
        );
        const affectedStores = [
          ...storesWithoutConfig.map(s => s.storeNumber ?? ''),
          ...disabledStoreConfigs.map(c => c.storeNumber),
        ];
        if (affectedStores.length > 0) {
          affectedStores.forEach(sn => storesToEnable.add(sn));
          itemsToEnable.push(current.itemDescription || current.itemNumber);
        }
      } else if (wasEnabled && !isNowEnabled) {
        // Disabling
        const storeConfigsToDisable = allStoreItemConfigs.filter(
          c => c.itemNumber === current.itemNumber && c.enabled &&
              c.franchiseName === selectedFranchise?.franchiseName,
        );
        if (storeConfigsToDisable.length > 0) {
          storeConfigsToDisable.forEach(c => storesToDisable.add(c.storeNumber));
          itemsToDisable.push(current.itemDescription || current.itemNumber);
        }
      }
    });
    if (storesToEnable.size === 0 && storesToDisable.size === 0) {
      return { needsConfirmation: false, message: '' };
    }
    const messages: string[] = [];
    if (storesToEnable.size > 0) {
      messages.push(t('confirmEnableItemsBatch', { 
        count: itemsToEnable.length, 
        storeCount: storesToEnable.size, 
      }));
    }
    if (storesToDisable.size > 0) {
      messages.push(t('confirmDisableItemsBatch', { 
        count: itemsToDisable.length, 
        storeCount: storesToDisable.size, 
      }));
    }
    return { needsConfirmation: true, message: messages.join('\n\n') };
  };

  /** Closes the cascade confirmation dialog. */
  const handleCancelConfirm = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const filteredConfigurations = useMemo(() => {
    const filtered = allConfigurations.filter(config =>
      search === '' ||
        config.itemNumber.toLowerCase().includes(search.toLowerCase()) ||
        config.itemDescription.toLowerCase().includes(search.toLowerCase()),
    );

    // Sort: enabled/half-enabled items first, disabled last
    // At franchise level, consider store state for half-enabled detection
    return filtered.sort((a, b) => {
      /** Returns a numeric sort weight: 0 = enabled, 1 = partially enabled, 2 = disabled. */
      const getSortWeight = (config: PodConfigurationModel): number => {
        if (configurationLevel === ConfigurationLevel.Franchise) {
          const storeState = getItemStoreEnabledState(config.itemNumber);
          const hasAnyStoreConfigs = allStoreItemConfigs.some(
            c => c.itemNumber === config.itemNumber && c.enabled && c.franchiseName === selectedFranchise?.franchiseName,
          );
          if (hasAnyStoreConfigs) {
            // Store reality is authoritative once any store-level config exists (even at
            // stores outside the currently-enabled set) — matches the switch's own display logic.
            return storeState === 'all' ? 0 : 1;
          }
          return config.enabled ? 0 : 2;            // No store-level presence at all — trust the franchise flag
        }
        return config.enabled ? 0 : 2;
      };
      return getSortWeight(a) - getSortWeight(b);
    });
  }, [allConfigurations, search, configurationLevel, allStoreItemConfigs, allStores, existingStoreConfigurations, selectedFranchise]);

  const isStoreEnabledForPredictiveOrdering = () => {
    // Check if the current store has predictive ordering enabled in franchise store configurations
    if (pickedStore && franchiseStoreConfigurations.length > 0) {
      const storeConfig = franchiseStoreConfigurations.find(
        config => config.storeNumber === pickedStore.storeNumber &&
                    !config.itemNumber, // Store-level configuration has no item number
      );
      return storeConfig?.enabled === true;
    }
    return false;
  };


  const isOnlyDigits = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return true;
    return /^\d+$/.test(String(value));
  };

  useEffect(() => {
    loadFranchises();
  }, []);

  useEffect(() => {
    loadStores();
  }, [selectedFranchise]);

  useEffect(() => {
    setAvailableConfigs(getAvailableConfigsFromItems(configurationLevel));
  }, [configurationLevel, allFranchises, allStores, allItems]);

  useEffect(() => {
    // Load franchise store configurations whenever we have a franchise
    // This is needed for isStoreEnabledForPredictiveOrdering() to work properly
    // even when we're at store level
    if (selectedFranchise) {
      loadFranchiseStoreConfigurations();
    }
  }, [selectedFranchise]);

  useEffect(() => {
    const configs = mergeArrays(availableConfigs, existingConfigurations);
    const sortedConfigs = (configs ?? []).sort((a, b) => Number(b.enabled) - Number(a.enabled));
    setAllConfigurations(sortedConfigs);
    setInitialConfigurations(sortedConfigs); // Store initial snapshot
  }, [availableConfigs, existingConfigurations]);


  /** Loads configurations and items for a given level and optional store. */
  const loadDataForLevel = async (configLevel: ConfigurationLevel, store: Store | null) => {
    setConfigurationLevel(configLevel);
    setPage(0);
    setIsLoading(true);

    const itemParams: ItemFilterParams = {
      marketName: selectedFranchise?.franchiseName ?? '',
      storeNumber: store?.storeNumber ?? '',
      search: '',
      pageNumber: 0,
      pageSize: 10000,
    };

    try {
      // Load items first, then configs
      const result = await client.getItemsByFranchiseName(itemParams);
      if (result.data) {
        const uniqueItems = result.data.items.reduce((acc: Item[], config) => {
          if (!acc.some(item => getItemNumber(item) === getItemNumber(config))) {
            acc.push(config);
          }
          return acc;
        }, []);
        setAllItems(uniqueItems);
      }

      // Now load configurations
      const res = await podClient.getConfigurations(configLevel, selectedFranchise?.franchiseName, store?.storeNumber);

      if (configLevel === ConfigurationLevel.Franchise) {
        setExistingConfigurations(res.filter(x => x.itemNumber));
        const storeConfigs = res.filter(x => !x.itemNumber);
        setExistingStoreConfigurations(storeConfigs);
        // Keep franchiseStoreConfigurations in sync so isStoreEnabledForPredictiveOrdering()
        // reflects any store enable/disable that just happened
        setFranchiseStoreConfigurations(storeConfigs);
        // Also load all store-level item configs for indeterminate state
        const storeItemConfigs = await podClient.getConfigurations(ConfigurationLevel.Store, selectedFranchise?.franchiseName, '');
        if (storeItemConfigs && storeItemConfigs.length > 0) {
          setAllStoreItemConfigs(storeItemConfigs.filter((x: PodConfigurationModel) => x.itemNumber));
        } else {
          setAllStoreItemConfigs([]);
        }
      } else {
        setExistingConfigurations(res);
        setAllStoreItemConfigs([]);
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConfigurations = (configLevel: ConfigurationLevel | null) => {
    setAllConfigurations([]);

    if (!configLevel) return;

    podClient.getConfigurations(configLevel, selectedFranchise?.franchiseName, pickedStore?.storeNumber)
      .then(res => {
        if (configLevel === ConfigurationLevel.Franchise) {
          setExistingConfigurations(res.filter(x => x.itemNumber));
          setExistingStoreConfigurations(res.filter(x => !x.itemNumber));
          return;
        }
        setExistingConfigurations(res);
      }).catch((error) => {
        logError(error);
      });
  };

  const loadFranchiseStoreConfigurations = () => {
    setFranchiseStoreConfigurations([]);

    if (!selectedFranchise?.franchiseName) return;

    // Load franchise-level store configurations (configurationLevel = 1 for Franchise)
    podClient.getConfigurations(ConfigurationLevel.Franchise, selectedFranchise.franchiseName, '')
      .then(res => {
        // Filter to get only store configurations (no itemNumber)
        const storeConfigs = res.filter(x => !x.itemNumber);
        setFranchiseStoreConfigurations(storeConfigs);
      }).catch((error) => {
        logError(error);
      });
  };

  const loadFranchises = () => {
    setIsLoading(true);
    getFranchises().then(result => {
      if (result.data) {
        setAllFranchises(result.data.sort((a, b) => compare(a, b, 'franchiseName', 'asc')));
        if (result.data.length === 1) {
          setSelectedFranchise(result.data[0]);
          // This triggers loadStores() via useEffect, which then calls loadDataForLevel()
        } else {
          // Multiple franchises or none: load Global level
          setConfigurationLevel(ConfigurationLevel.Global);
          loadConfigurations(ConfigurationLevel.Global);
        }
      }
    }).catch((error) => {
      logError(error);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const loadStores = () => {
    setAllStores([]);
    setPickedStore(null);
    setAllItems([]);
    setAllConfigurations([]);
    setExistingConfigurations([]);
    setExistingStoreConfigurations([]);
    setAllStoreItemConfigs([]);

    if (!selectedFranchise?.franchiseName) {
      setConfigurationLevel(ConfigurationLevel.Global);
      loadConfigurations(ConfigurationLevel.Global);
      return;
    }

    // Use getFranchiseStores for everyone (stores have franchiseName populated)
    getFranchiseStores(selectedFranchise?.franchiseName)
      .then((result) => {
        let stores = (result.data?.stores ?? [])
          .filter(x => x.starbucksStoreNumber)
          .sort((a, b) => compare(a, b, 'storeName', 'asc'));

        // Non-admin: filter to only stores the user has access to
        if (!isAdmin()) {
          const userStoreNumbers = new Set(
            (user?.stores ?? []).map(s => s.storeNumber),
          );
          stores = stores.filter(s => userStoreNumbers.has(s.storeNumber));
        }

        setAllStores(stores);

        if (isAdmin()) {
          // Admin: no store preselection, load at Franchise level
          loadDataForLevel(ConfigurationLevel.Franchise, null);
        } else {
          // Non-admin: preselect a store
          const currentStoreNumber = selectedStore?.storeNumber;
          const currentStore = stores.find(s => s.storeNumber === currentStoreNumber);
          const storeToUse = currentStore ?? stores[0] ?? null;
          setPickedStore(storeToUse);

          if (storeToUse) {
            loadDataForLevel(ConfigurationLevel.Store, storeToUse);
          } else {
            loadDataForLevel(ConfigurationLevel.Franchise, null);
          }
        }
      })
      .catch((error) => {
        logError(error);
        loadDataForLevel(ConfigurationLevel.Franchise, null);
      });
  };

  /** Saves all changed configurations with cascade operations. */
  const handleSaveAll = async () => {
    const changedConfigs = changedConfigurations;
    if (changedConfigs.length === 0) return;
    setIsLoading(true);
    setConfirmDialog(prev => ({ ...prev, open: false }));

    // Collect all creates and updates across all changed configs, deduped by config key
    const toCreateMap = new Map<string, PodConfigurationModel>();
    const toUpdateMap = new Map<string, PodConfigurationModel>();

    // Clear validation errors for all changed configs in one update
    const keysToRemove = new Set(changedConfigs.map(item => `${item.franchiseName}-${item.storeNumber}-${item.itemNumber}`));
    setValidationErrors(prev =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => !keysToRemove.has(key))),
    );

    for (const item of changedConfigs) {
      let normalizedItem = normalizeQuantityFields(item);

      // CASE: Enabling/disabling an ITEM at franchise level → cascade to enabled stores
      if (normalizedItem.configurationLevel === ConfigurationLevel.Franchise && normalizedItem.itemNumber) {
        normalizedItem = { ...normalizedItem, storeName: '', storeNumber: '', starbucksStoreNumber: '' };

        const initialConfig = initialConfigurations.find(c => getConfigKey(c) === getConfigKey(normalizedItem));
        const wasEnabled = initialConfig?.enabled ?? false;

        if (normalizedItem.enabled !== wasEnabled) {
          const ops = buildItemCascadeOps(normalizedItem);
          ops.toCreate.forEach(c => toCreateMap.set(getConfigKey(c), c));
          ops.toUpdate.forEach(c => toUpdateMap.set(getConfigKey(c), c));
        }
      }

      // CASE: Enabling/disabling a STORE at franchise level → cascade item configs
      if (normalizedItem.configurationLevel === ConfigurationLevel.Franchise && !normalizedItem.itemNumber) {
        const ops = buildStoreCascadeOps(normalizedItem);
        ops.toCreate.forEach(c => toCreateMap.set(getConfigKey(c), c));
        ops.toUpdate.forEach(c => toUpdateMap.set(getConfigKey(c), c));
      }

      // The config itself
      if (normalizedItem.id > 0) {
        toUpdateMap.set(getConfigKey(normalizedItem), normalizedItem);
      } else {
        // Skip creating a franchise config with enabled=false if it doesn't exist yet
        if (normalizedItem.configurationLevel === ConfigurationLevel.Franchise && !normalizedItem.enabled && normalizedItem.id === 0) {
          continue;
        }
        toCreateMap.set(getConfigKey(normalizedItem), normalizedItem);
      }
    }

    try {
      // Execute all operations in parallel as batches
      const toCreate = Array.from(toCreateMap.values());
      const toUpdate = Array.from(toUpdateMap.values());
      const operations: Promise<unknown>[] = [];
      if (toCreate.length > 0) operations.push(podClient.createBatch(toCreate));
      if (toUpdate.length > 0) operations.push(podClient.updateBatch(toUpdate));
      await Promise.all(operations);

      await loadDataForLevel(configurationLevel ?? ConfigurationLevel.Global, pickedStore);
      setShowSuccessMessage(true);
    } catch (error) {
      logError(error);
      setErrorMessage(t('errorUpdating'));
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Global save handler with cascade confirmation
  const handleSaveAllWithConfirmation = async () => {
    const cascadeInfo = getAggregatedCascadeInfo();
    if (cascadeInfo.needsConfirmation) {
      setConfirmDialog({ open: true, message: cascadeInfo.message });
    } else {
      await handleSaveAll();
    }
  };

  const getAvailableConfigsFromItems = (configurationLevel: ConfigurationLevel | null) => {
    return allItems.map((x) => {
      const config: PodConfigurationModel = {
        id: 0,
        franchiseName: selectedFranchise?.franchiseName ?? '',
        itemNumber: getItemNumber(x),
        itemDescription: x.name ?? '',
        configurationLevel: configurationLevel ?? ConfigurationLevel.Store,
        minStockOnHandQuantity: null,
        maxStockOnHandQuantity: null,
        safetyStock: null, // Safety stock is now automatically calculated by the backend
        isParEnabled: false,
        enabled: false,
        storeNumber: '',
        storeName: '',
        starbucksStoreNumber: '',
        franchiseStores: [],
      };

      if (configurationLevel === ConfigurationLevel.Store) {
        config.storeNumber = pickedStore?.storeNumber ?? '';
        config.storeName = pickedStore?.storeName ?? '';
        config.starbucksStoreNumber = pickedStore?.starbucksStoreNumber ?? '';
      }

      return config;
    });
  };

  const handleFranchiseChange = (event: SyntheticEvent, value: Franchise | null) => {
    setSelectedFranchise(value);
  };

  const handleStoreChange = (event: SyntheticEvent, value: Store | null) => {
    setPickedStore(value);

    if (value) {
      loadDataForLevel(ConfigurationLevel.Store, value);
    } else {
      loadDataForLevel(ConfigurationLevel.Franchise, null);
    }
  };

  const handleConfigChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const { name, value } = event.target;
    const actualIndex = getActualIndex(index);
    const isValidNumber = /^\d*$/.test(value);
    if (value !== '' && !isValidNumber) return;
    setAllConfigurations(prev => {
      const updated = [...prev];
      updated[actualIndex] = { 
        ...updated[actualIndex], 
        [name]: value === '' ? '' : value, 
      };
      return updated;
    });
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, checked } = event.target;
    const actualIndex = getActualIndex(index);
    setAllConfigurations(prev => {
      const updated = [...prev];
      updated[actualIndex] = { ...updated[actualIndex], [name]: checked };
      return updated;
    });
  };

  /** Toggles a franchise-level item between enabled and disabled, accounting for indeterminate state. */
  const handleFranchiseItemToggle = (index: number, isIndeterminate: boolean) => {
    const actualIndex = getActualIndex(index);
    setAllConfigurations(prev => {
      const updated = [...prev];
      const current = updated[actualIndex];
      // If indeterminate (some stores have it), clicking always disables.
      // User can re-enable from the off state to push to all stores.
      updated[actualIndex] = { ...current, enabled: isIndeterminate ? false : !current.enabled };
      return updated;
    });
  };

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setSearch(newSearchValue);
    setPage(0); // Reset to first page when search changes
  };

  // Validation functions
  const validateMinMaxStock = (config: PodConfigurationModel): string | null => {
    // Default min to 0 if not configured
    const min = config.minStockOnHandQuantity ? Number(config.minStockOnHandQuantity) : 0;
    const max = Number(config.maxStockOnHandQuantity);

    // Skip validation if both fields are empty/null
    if (!config.minStockOnHandQuantity && !config.maxStockOnHandQuantity) {
      return null;
    }

    // Get pack size for validation
    const item = allItems.find(x => x.sku === config.itemNumber);
    const packSize = item?.packSize ? Number(item.packSize) : null;

    // Validate max >= min when both provided
    if (config.minStockOnHandQuantity && config.maxStockOnHandQuantity && max < min) {
      return t('maxStockMustBeHigherThanMin');
    }

    // Validate maxStockOnHandQuantity >= packSize + min (min defaults to 0)
    if (config.maxStockOnHandQuantity && packSize && !isNaN(packSize)) {
      const minimumRequired = packSize + min;
      if (max < minimumRequired) {
        return t('maxStockMustBeAtLeastConversionPlusMin', { value: minimumRequired });
      }
    }

    return null;
  };

  const getValidationError = (config: PodConfigurationModel): string | null => {
    if (configurationLevel === ConfigurationLevel.Store) {
      return validateMinMaxStock(config);
    }
    return null;
  };

  const shouldDisableGlobalSave = useMemo((): boolean => {
    return changedConfigurations.some(config => {
      const error = getValidationError(config);
      return Boolean(error) || !isOnlyDigits(config.minStockOnHandQuantity) || !isOnlyDigits(config.maxStockOnHandQuantity);
    });
  }, [changedConfigurations]);

  const handleSave = useCallback(async (item: PodConfigurationModel) => {
    // Validate before saving
    const validationError = getValidationError(item);
    if (validationError) {
      const errorKey = `${item.franchiseName}-${item.storeNumber}-${item.itemNumber}`;
      setValidationErrors(prev => ({ ...prev, [errorKey]: validationError }));
      return;
    }

    // Clear any existing validation errors for this item
    const errorKey = `${item.franchiseName}-${item.storeNumber}-${item.itemNumber}`;
    setValidationErrors(prev =>
      Object.fromEntries(Object.entries(prev).filter(([key]) => key !== errorKey)),
    );

    setIsLoading(true);
    const initialConfig = initialConfigurations.find(c =>
      c.franchiseName === item.franchiseName &&
      c.storeNumber === item.storeNumber &&
      c.itemNumber === item.itemNumber,
    );
    const isCurrentlyEnabled = initialConfig?.enabled ?? false;

    let normalizedItem = normalizeQuantityFields(item);

    // Collect all configs to batch
    const toCreate: PodConfigurationModel[] = [];
    const toUpdate: PodConfigurationModel[] = [];

    // CASE: Enabling/disabling a STORE (no itemNumber) → cascade item configs
    if (normalizedItem.configurationLevel === ConfigurationLevel.Franchise && !normalizedItem.itemNumber) {
      const ops = buildStoreCascadeOps(normalizedItem);
      toCreate.push(...ops.toCreate);
      toUpdate.push(...ops.toUpdate);
    }

    // CASE: Enabling/disabling an ITEM at franchise level → cascade to enabled stores
    if (normalizedItem.configurationLevel === ConfigurationLevel.Franchise && normalizedItem.itemNumber) {
      normalizedItem = { ...normalizedItem, storeName: '', storeNumber: '', starbucksStoreNumber: '' };

      const storeState = getItemStoreEnabledState(normalizedItem.itemNumber);
      const shouldCascade = normalizedItem.enabled || (!normalizedItem.enabled && storeState !== 'none');

      if (shouldCascade) {
        const ops = buildItemCascadeOps(normalizedItem);
        toCreate.push(...ops.toCreate);
        toUpdate.push(...ops.toUpdate);
      }

      // If no franchise-level config exists and we just need to disable stores, skip the main item save
      if (!normalizedItem.enabled && normalizedItem.id === 0) {
        try {
          if (toUpdate.length > 0) await podClient.updateBatch(toUpdate);
          await loadDataForLevel(configurationLevel ?? ConfigurationLevel.Global, pickedStore);
          setShowSuccessMessage(true);
        } catch (error) {
          logError(error);
          setErrorMessage(t('errorUpdating'));
          setShowErrorMessage(true);
        } finally {
          setIsLoading(false);
        }
        return;
      }
    }

    // Add the main item itself
    if (normalizedItem.id > 0) {
      toUpdate.push(normalizedItem);
    } else {
      toCreate.push(normalizedItem);
    }

    try {
      // Execute batch operations in parallel
      const operations: Promise<unknown>[] = [];
      if (toCreate.length > 0) operations.push(podClient.createBatch(toCreate));
      if (toUpdate.length > 0) operations.push(podClient.updateBatch(toUpdate));
      await Promise.all(operations);

      // CASCADE-UP: When enabling a store-level item, auto-create franchise config if missing
      if (normalizedItem.configurationLevel === ConfigurationLevel.Store && normalizedItem.itemNumber && normalizedItem.enabled) {
        try {
          const franchiseConfigs = await podClient.getConfigurations(
            ConfigurationLevel.Franchise, normalizedItem.franchiseName, '',
          );
          const franchiseItemConfig = franchiseConfigs.find(
            c => c.itemNumber === normalizedItem.itemNumber,
          );
          if (!franchiseItemConfig) {
            await podClient.createBatch([{
              ...normalizedItem,
              id: 0,
              configurationLevel: ConfigurationLevel.Franchise,
              storeNumber: '',
              storeName: '',
              starbucksStoreNumber: '',
            }]);
          } else if (!franchiseItemConfig.enabled) {
            await podClient.updateBatch([{ ...franchiseItemConfig, enabled: true }]);
          }
        } catch (cascadeError) {
          logError(`Cascade-up (enable franchise config) failed: ${cascadeError}`);
        }
      }

      // CASCADE-UP: When disabling a store-level item, auto-disable franchise config if no stores left
      if (normalizedItem.configurationLevel === ConfigurationLevel.Store && normalizedItem.itemNumber && !normalizedItem.enabled) {
        try {
          const allStoreConfigs = await podClient.getConfigurations(
            ConfigurationLevel.Store, normalizedItem.franchiseName, '',
          );
          const stillEnabledAtOtherStores = allStoreConfigs.some(
            c => c.itemNumber === normalizedItem.itemNumber && c.enabled,
          );
          if (!stillEnabledAtOtherStores) {
            const franchiseConfigs = await podClient.getConfigurations(
              ConfigurationLevel.Franchise, normalizedItem.franchiseName, '',
            );
            const franchiseItemConfig = franchiseConfigs.find(
              c => c.itemNumber === normalizedItem.itemNumber && c.enabled,
            );
            if (franchiseItemConfig) {
              await podClient.updateBatch([{ ...franchiseItemConfig, enabled: false }]);
            }
          }
        } catch (cascadeError) {
          logError(`Cascade-up check failed: ${cascadeError}`);
        }
      }

      await loadDataForLevel(configurationLevel ?? ConfigurationLevel.Global, pickedStore);
      if (!isCurrentlyEnabled && normalizedItem.enabled && normalizedItem.itemNumber) setIsPodInfoModalOpen(true);
      setShowSuccessMessage(true);
    } catch (error) {
      logError(error);
      setErrorMessage(t('errorUpdating'));
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  }, [initialConfigurations, configurationLevel, pickedStore, selectedFranchise, allStoreItemConfigs, existingConfigurations]);

  const handleBatchStoreSave = useCallback(async (configs: PodConfigurationModel[]) => {
    if (configs.length === 0) return;
    setIsLoading(true);

    try {
      // Collect all store configs to create and update
      const toCreate: PodConfigurationModel[] = [];
      const toUpdate: PodConfigurationModel[] = [];

      for (const item of configs) {
        // Cascade store enable/disable to item configs
        const ops = buildStoreCascadeOps(item);
        toCreate.push(...ops.toCreate);
        toUpdate.push(...ops.toUpdate);

        // The store config itself
        if (item.id > 0) {
          toUpdate.push(item);
        } else {
          toCreate.push(item);
        }
      }

      // Execute all batch operations in parallel
      const operations: Promise<unknown>[] = [];
      if (toCreate.length > 0) {
        operations.push(podClient.createBatch(toCreate));
      }
      if (toUpdate.length > 0) {
        operations.push(podClient.updateBatch(toUpdate));
      }

      await Promise.all(operations);

      await loadDataForLevel(configurationLevel ?? ConfigurationLevel.Global, pickedStore);
      setShowSuccessMessage(true);
    } catch (error) {
      logError(error);
      setErrorMessage(t('errorUpdating'));
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  }, [configurationLevel, pickedStore, buildStoreCascadeOps]);

  const handleClear = () => {
    setSearch('');
    setPage(0);
  };

  const handleRowsPerPageChange = (e: { target: { value: string | number } }) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
  };

  const handleClosePodInfoModal = () => setIsPodInfoModalOpen(false);
  const handleCloseSuccessMessage = () => setShowSuccessMessage(false);
  const handleCloseErrorMessage = () => setShowErrorMessage(false);

  const getItemUom = (itemNumber: string) => {
    const item = allItems.find(x => x.sku === itemNumber);
    const primaryUom = item?.primaryUom;
    const orderableUom = item?.orderableUom;
    return primaryUom ?? orderableUom ?? 'EA';
  };


  // Helper function to get paginated configurations for display
  const getVisibleConfigurations = () => {
    const startIndex = page * rowsPerPage;
    return filteredConfigurations.slice(startIndex, startIndex + rowsPerPage);
  };

  // Helper function to get max pages based on total configurations
  const getMaxPages = () => Math.ceil(filteredConfigurations.length / rowsPerPage);

  // Helper function to convert visible index to actual array index
  const getActualIndex = (visibleIndex: number) => {
    // Use the same filtered+sorted list that is displayed to the user
    const actualIndex = page * rowsPerPage + visibleIndex;
    const targetConfig = filteredConfigurations[actualIndex];

    // Find the actual index in the full configurations array
    return allConfigurations.findIndex(config =>
      config.itemNumber === targetConfig?.itemNumber &&
            config.franchiseName === targetConfig?.franchiseName &&
            config.storeNumber === targetConfig?.storeNumber,
    );
  };

  const renderData = () => {
    // Only show min/max stock on hand columns for item+store level (configurationLevel === ConfigurationLevel.Store)
    const showMinMaxStockColumns = configurationLevel === ConfigurationLevel.Store;

    // Pre-compute values that are the same for every row
    const totalStoresCount = configurationLevel === ConfigurationLevel.Franchise
      ? getEnabledStores().length : 0;
    const initialMap = new Map(initialConfigurations.map(c => [getConfigKey(c), c]));

    // Check if store is enabled for Predictive Ordering
    if (configurationLevel === ConfigurationLevel.Store && !isStoreEnabledForPredictiveOrdering()) {
      return (
        <Typography variant="textMD"
          sx={{ display: 'flex', flex: 1, justifyContent: 'center', p: 4 }}>
          {t('predictiveOrderingNotEnabledForStore')}
        </Typography>
      );
    }

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200, maxWidth: 400 }}>
            <TextField
              placeholder={t('search')}
              size='small'
              autoComplete='off'
              id="search-input"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon><SearchSm /></SvgIcon>
                  </InputAdornment>
                ),
              }}
              value={search}
              onChange={handleSearchChange}
              fullWidth
            />
            {search && (
              <Button
                variant="secondary"
                size="md"
                onClick={handleClear}
                sx={{ whiteSpace: 'nowrap' }}>
                {t('clear')}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {hasAnyPendingChanges && (
              <Typography
                component="span"
                onClick={handleCancelAll}
                sx={{
                  color: 'text.secondary',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {t('cancelAll')}
              </Typography>
            )}
            <Button
              variant="primary"
              size="lg"
              onClick={handleSaveAllWithConfirmation}
              disabled={!hasAnyPendingChanges || shouldDisableGlobalSave}>
              {t('saveAll', { count: changedConfigurations.length })}
            </Button>
          </Box>
        </Box>
        <TableContainer sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('itemNumber')}</TableCell>
                <TableCell>{t('description')}</TableCell>
                <TableCell sx={{ textAlign: 'center', width: 80 }}>{t('enabled')}</TableCell>
                {configurationLevel === ConfigurationLevel.Store && (
                  <TableCell sx={{ textAlign: 'center', width: 120 }}>{t('parEnabled')}</TableCell>
                )}
                {showMinMaxStockColumns && (
                  <TableCell sx={{ textAlign: 'center', width: 140 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <span>{t('minStockLevel')}</span>
                      <Tooltip
                        title={t('minStockTooltip')}
                        arrow
                        placement="top"
                      >
                        <HelpOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
                {showMinMaxStockColumns && (
                  <TableCell sx={{ textAlign: 'center', width: 140 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <span>{t('maxStockLevel')}</span>
                      <Tooltip
                        title={t('maxStockTooltip')}
                        arrow
                        placement="top"
                      >
                        <HelpOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {getVisibleConfigurations().map((row, index) => {
                const validationError = getValidationError(row);
                const digitsOnlyError = t('digitsOnlyError');
                const isStoreItemLevel = configurationLevel === ConfigurationLevel.Store;
                const minStockDigitsError = isStoreItemLevel && !isOnlyDigits(row.minStockOnHandQuantity);
                const maxStockDigitsError = isStoreItemLevel && !isOnlyDigits(row.maxStockOnHandQuantity);
                const minStockErrorMessage = minStockDigitsError
                  ? digitsOnlyError
                  : validationError;
                const maxStockErrorMessage = maxStockDigitsError
                  ? digitsOnlyError
                  : validationError;
                const showMinMax = showMinMaxStockColumns;
                const storeState = getItemStoreEnabledState(row.itemNumber);

                // Detect pending local change to the enabled flag
                const initialConfig = configurationLevel === ConfigurationLevel.Franchise
                  ? initialMap.get(getConfigKey(row))
                  : undefined;
                const hasLocalEnabledChange = initialConfig
                  ? initialConfig.enabled !== row.enabled
                  : false;

                // Detect any pending change for row highlighting
                const initial = initialMap.get(getConfigKey(row));
                const isRowChanged = !initial ||
                  JSON.stringify(normalizeForCompare(row)) !== JSON.stringify(normalizeForCompare(initial));

                // Pre-compute store counts for franchise-level display
                const enabledStoresForItem = configurationLevel === ConfigurationLevel.Franchise
                  ? allStoreItemConfigs.filter(
                    c => c.itemNumber === row.itemNumber && c.enabled && c.franchiseName === selectedFranchise?.franchiseName,
                  ).length : 0;

                // When user has toggled, show their explicit choice;
                // otherwise derive visual state from franchise flag + store reality.
                // Key: if there's no franchise config (row.enabled=false from template),
                // but stores DO have it enabled, we still show the store reality.
                const hasFranchiseConfig = row.id > 0;
                const hasAnyStoreConfigs = enabledStoresForItem > 0;

                let switchChecked: boolean;
                let switchIndeterminate: boolean;

                if (configurationLevel === ConfigurationLevel.Franchise) {
                  if (hasLocalEnabledChange) {
                    // User explicitly toggled — show their choice
                    switchChecked = row.enabled;
                    switchIndeterminate = false;
                  } else if (hasAnyStoreConfigs) {
                    // Some stores have the item enabled — derive from store reality
                    switchChecked = storeState === 'all';
                    switchIndeterminate = storeState !== 'all';
                  } else if (hasFranchiseConfig && row.enabled) {
                    // Franchise config enabled but no stores have it yet
                    switchChecked = true;
                    switchIndeterminate = false;
                  } else {
                    // Not enabled anywhere
                    switchChecked = false;
                    switchIndeterminate = false;
                  }
                } else {
                  switchChecked = row.enabled;
                  switchIndeterminate = false;
                }

                return (
                  <React.Fragment key={`${row.franchiseName}-${row.storeNumber}-${row.itemNumber}`}>
                    <TableRow sx={isRowChanged ? {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      '& > td:first-of-type': { borderLeft: `3px solid ${theme.palette.primary.main}` },
                    } : undefined}>
                      <TableCell>{row.itemNumber}</TableCell>
                      <TableCell
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.itemDescription}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Tooltip
                            title={
                              configurationLevel === ConfigurationLevel.Franchise && switchIndeterminate
                                ? t('storesEnabledCount', { enabled: enabledStoresForItem, total: totalStoresCount })
                                : ''
                            }
                            arrow
                            placement="top"
                            disableHoverListener={!(configurationLevel === ConfigurationLevel.Franchise && switchIndeterminate)}
                          >
                            <span>
                              <CustomSwitch
                                checked={switchChecked}
                                indeterminate={switchIndeterminate}
                                name='enabled'
                                disabled={!hasPermissionTo(['AIOrderingAdmin'])}
                                onChange={($event: ChangeEvent<HTMLInputElement>) =>
                                  configurationLevel === ConfigurationLevel.Franchise
                                    ? handleFranchiseItemToggle(index, switchIndeterminate)
                                    : handleCheckboxChange($event, index)
                                }
                              />
                            </span>
                          </Tooltip>
                          {configurationLevel === ConfigurationLevel.Franchise && switchIndeterminate && (
                            <Typography color="text.secondary"
                              sx={{ fontSize: '0.65rem', lineHeight: 1.2, mt: 0.75, whiteSpace: 'nowrap' }}>
                              {enabledStoresForItem}/{totalStoresCount}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      {configurationLevel === ConfigurationLevel.Store && (
                        <TableCell sx={{ textAlign: 'center' }}>
                          <CustomSwitch checked={row.isParEnabled}
                            name='isParEnabled'
                            disabled={!hasPermissionTo(['AIOrderingAdmin']) || !row.enabled}
                            onChange={($event: ChangeEvent<HTMLInputElement>) => handleCheckboxChange($event, index)} />
                        </TableCell>
                      )}
                      {showMinMax && (
                        <TableCell sx={{ textAlign: 'center', width: 140 }}>
                          <TextField
                            type="number"
                            name="minStockOnHandQuantity"
                            value={row.minStockOnHandQuantity ?? ''}
                            placeholder={
                              configurationLevel === ConfigurationLevel.Store && row.safetyStock != null
                                ? String(Math.round(Number(row.safetyStock)))
                                : undefined
                            }
                            disabled={!row.enabled}
                            onChange={($event: ChangeEvent<HTMLInputElement>) => handleConfigChange($event, index)}
                            size="small"
                            error={Boolean(minStockErrorMessage)}
                            inputProps={{
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                              min: 0,
                              readOnly: !canEditConfigFields,
                              'aria-label': !canEditConfigFields ? t('readOnlyField') : undefined,
                            }}
                            sx={{
                              '& .MuiInputBase-input::placeholder': { fontStyle: 'italic' },
                              ...(!canEditConfigFields ? {
                                backgroundColor: theme.palette.custom.gray[100],
                                '& .MuiInputBase-input': {
                                  color: theme.palette.custom.gray[400],
                                },
                              } : {}),
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {Boolean(minStockErrorMessage) && (
                                      <Tooltip
                                        title={minStockErrorMessage}
                                        arrow
                                        placement="top"
                                      >
                                        <ErrorOutline sx={{ fontSize: 16, color: 'error.main', cursor: 'help' }} />
                                      </Tooltip>
                                    )}
                                    {getItemUom(row.itemNumber)}
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                      )}
                      {showMinMax && (
                        <TableCell sx={{ textAlign: 'center', width: 140 }}>
                          <TextField
                            type="number"
                            name="maxStockOnHandQuantity"
                            value={row.maxStockOnHandQuantity ?? ''}
                            disabled={!row.enabled}
                            onChange={($event: ChangeEvent<HTMLInputElement>) => handleConfigChange($event, index)}
                            size="small"
                            error={Boolean(maxStockErrorMessage)}
                            inputProps={{
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                              min: 0,
                              readOnly: !canEditConfigFields,
                              'aria-label': !canEditConfigFields ? t('readOnlyField') : undefined,
                            }}
                            sx={!canEditConfigFields ? {
                              backgroundColor: theme.palette.custom.gray[100],
                              '& .MuiInputBase-input': {
                                color: theme.palette.custom.gray[400],
                              },
                            } : undefined}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {Boolean(maxStockErrorMessage) && (
                                      <Tooltip
                                        title={maxStockErrorMessage}
                                        arrow
                                        placement="top"
                                      >
                                        <ErrorOutline sx={{ fontSize: 16, color: 'error.main', cursor: 'help' }} />
                                      </Tooltip>
                                    )}
                                    {getItemUom(row.itemNumber)}
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="textSM"
              color="text.secondary">{t('rowsPerPage')}</Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              sx={{ minWidth: 70, alignSelf: 'center' }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </Box>
          <CustomPagination
            page={page}
            setPage={handlePageChange}
            maxPages={getMaxPages()}
            breakpointForChangeDisplay={120000}
          />
        </Box>
      </>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return <LoadingWheel />;
    }

    if (configurationLevel === ConfigurationLevel.Global) {
      return (
        <FranchiseConfigurationsList franchises={allFranchises}
          existingFranchiseConfigurations={existingConfigurations}
          canToggleEnabled={canToggleEnabled}
          handleSave={handleSave}></FranchiseConfigurationsList>
      );
    }

    if (configurationLevel === ConfigurationLevel.Franchise) {
      return (
        <>
          <Accordion sx={{ mb: 4 }}
            defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Typography variant="textMD"
                  sx={{ fontWeight: 600 }}>
                  {t('storeConfigurations')}
                </Typography>
                <Chip
                  size="small"
                  label={`${getEnabledStores().length} / ${allStores.length}`}
                  color={getEnabledStores().length > 0 ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pb: 3 }}>
              {selectedFranchise && (
                <StoreConfigurationsList
                  franchiseName={selectedFranchise?.franchiseName ?? ''}
                  stores={allStores}
                  existingStoreConfigurations={existingStoreConfigurations}
                  canToggleEnabled={canToggleEnabled}
                  handleBatchSave={handleBatchStoreSave}
                />
              )}
            </AccordionDetails>
          </Accordion>
          <Box sx={{ mt: 3 }}>
            {renderData()}
          </Box>
        </>
      );
    }

    if (allConfigurations.length === 0) {
      return (
        <Typography variant="textMD"
          sx={{ display: 'flex', flex: 1, justifyContent: 'flex-start' }}>{t('noData')}
        </Typography>
      );
    }

    return renderData();
  };

  return (
    <PageArea>
      <Box
        sx={{
          textAlign: 'left',
          color: 'text.primary',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          alignSelf: 'stretch',
        }}>
        <PageTitle>{' ' + t('title')}</PageTitle>
      </Box>
      <Grid container
        sx={{ mb: '12px' }}
        alignItems={'center'}
        spacing={5}>
        <Grid item
          xs={12}
          md={6}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              options={allFranchises}
              value={selectedFranchise}
              onChange={handleFranchiseChange}
              getOptionKey={(option) => option.franchiseName ?? ''}
              getOptionLabel={(option) => option.franchiseName ?? ''}
              disabled={allFranchises.length <= 1}
              renderInput={(params) => (
                <TextField {...params}
                  label={t('franchise')}
                  placeholder={t('selectFranchise')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          xs={12}
          md={6}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              options={allStores}
              value={pickedStore}
              onChange={handleStoreChange}
              getOptionKey={(option) => option.storeNumber ?? ''}
              getOptionLabel={(option) => option.storeName ?? ''}
              disableClearable={isStoreManager}
              renderInput={(params) => (
                <TextField {...params}
                  label={t('store')}
                  placeholder={t('selectStore')} />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{
        display: 'flex', flexDirection: 'column', width: '100%', height: '100%', p: '12px', border: `solid 1px ${theme.palette.custom.gray[200]}`,
        borderRadius: '4px',
      }}>
        {renderContent()}
      </Box>
      <PodConfigurationInfo
        open={isPodInfoModalOpen}
        onClose={handleClosePodInfoModal}
      />
      <MessagesSnackbar
        open={showSuccessMessage}
        onClose={handleCloseSuccessMessage}
        message={t('successfullyUpdated')}
        severity="success"
        duration={3000}
      />
      <MessagesSnackbar
        open={showErrorMessage}
        onClose={handleCloseErrorMessage}
        message={errorMessage || t('errorUpdating')}
        severity="error"
        duration={null}
      />

      {/*Confirmation Dialog for Cascade Actions */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelConfirm}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {t('confirmCascadeTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description"
            sx={{ whiteSpace: 'pre-line' }}>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm}
            variant="secondary">
            {t('cancel')}
          </Button>
          <Button onClick={handleSaveAll}
            variant="primary">
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default ManagePodConfigurations;